/**
 * Charts.
 *
 * Plain markup, no charting library. One twelve-bar comparison does not justify hundreds of
 * kilobytes on every page load, and the field agent on a slow connection pays for that weight
 * on every visit.
 *
 * The only chart on the dashboard is billed against collected, because the gap between the two
 * is the arrears nobody has chased yet — which is the one trend a manager acts on.
 */

import { monthLabel } from "@rentorng/core";

export interface Point {
  key: string;
  billed: number;
  collected: number;
}

export function BilledVsCollected({ points, format }: { points: Point[]; format: (minor: number) => string }) {
  const max = Math.max(1, ...points.flatMap((p) => [p.billed, p.collected]));

  return (
    <div>
      <div className="flex h-40 items-end gap-1" role="img" aria-label="Billed against collected, by month">
        {points.map((point) => (
          <div
            key={point.key}
            className="flex h-full flex-1 items-end justify-center gap-0.5"
            title={`${monthLabel(point.key)} · billed ${format(point.billed)} · collected ${format(point.collected)}`}
          >
            <div
              className="w-1/2 rounded-t-sm bg-ink-200"
              style={{ height: `${Math.max((point.billed / max) * 100, point.billed > 0 ? 2 : 0)}%` }}
            />
            <div
              className="w-1/2 rounded-t-sm bg-brand-500"
              style={{ height: `${Math.max((point.collected / max) * 100, point.collected > 0 ? 2 : 0)}%` }}
            />
          </div>
        ))}
      </div>

      <div className="mt-1.5 flex gap-1 text-[10px] text-ink-400">
        {points.map((point) => (
          <div key={point.key} className="flex-1 truncate text-center">
            {monthLabel(point.key).split(" ")[0]}
          </div>
        ))}
      </div>

      <div className="mt-3 flex gap-4 text-xs text-ink-500">
        <span className="flex items-center gap-1.5">
          <span className="size-2.5 rounded-sm bg-ink-200" /> Billed
        </span>
        <span className="flex items-center gap-1.5">
          <span className="size-2.5 rounded-sm bg-brand-500" /> Collected
        </span>
      </div>
    </div>
  );
}

/** A single proportion bar — occupancy, collection rate, quota used. */
export function MeterBar({
  value,
  max,
  tone = "brand",
  label,
}: {
  value: number;
  max: number;
  tone?: "brand" | "warn" | "danger";
  label?: string;
}) {
  const pct = max <= 0 ? 0 : Math.min(100, Math.round((value / max) * 100));
  const fill = tone === "danger" ? "bg-danger-500" : tone === "warn" ? "bg-warn-500" : "bg-brand-500";
  return (
    <div>
      {label ? (
        <div className="mb-1 flex justify-between text-xs text-ink-500">
          <span>{label}</span>
          <span className="tabular">{pct}%</span>
        </div>
      ) : null}
      <div className="h-2 w-full overflow-hidden rounded-full bg-ink-200">
        <div className={`h-full ${fill}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

/** A horizontal breakdown — arrears by age, collections by method. */
export function StackedBar({ segments }: { segments: Array<{ label: string; value: number; className: string }> }) {
  const total = segments.reduce((sum, s) => sum + s.value, 0);
  if (total <= 0) return <div className="h-2 w-full rounded-full bg-ink-200" />;

  return (
    <div>
      <div className="flex h-2.5 w-full overflow-hidden rounded-full bg-ink-200">
        {segments.map((segment) =>
          segment.value > 0 ? (
            <div
              key={segment.label}
              className={segment.className}
              style={{ width: `${(segment.value / total) * 100}%` }}
              title={segment.label}
            />
          ) : null,
        )}
      </div>
      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-ink-500">
        {segments.map((segment) => (
          <span key={segment.label} className="flex items-center gap-1.5">
            <span className={`size-2.5 rounded-sm ${segment.className}`} />
            {segment.label}
          </span>
        ))}
      </div>
    </div>
  );
}
